<template>
    <CompassModal
        v-model:visible="visible"
        :primaryAction="primaryAction"
        :secondaryActions="secondariesActions"
        :title="$t.__('common.actions.buy_credits')"
        size="small"
        @closed="onClose"
    >
        <template v-if="loading" #default>
            <CompassLoader class="preloader-container__loader" />
        </template>
        <template v-else #default>
            <p class="typographies-paragraph-medium-regular chargebee-quota-dialog__subtitle">
                {{
                    $l10n.__(
                        'Définissez le nombre de packs de crédits que vous souhaitez acheter en fonction de vos besoins.',
                    )
                }}
            </p>
            <div
                v-for="credit in dialogCredits"
                :key="credit.id"
                class="chargebee-quota-dialog__credit"
            >
                <div class="chargebee-quota-dialog__credit-title">
                    <div class="chargebee-quota-dialog__credit-title-name">
                        <CompassIcon :icon="credit.icon" />
                        <span class="typographies-paragraph-medium-bold"> {{ credit.title }} </span>
                    </div>
                    <span
                        class="typographies-paragraph-small-semibold"
                        :class="{
                            'chargebee-quota-dialog__credit-title-remaining--none':
                                credit.remaining === 0,
                        }"
                    >
                        {{
                            $l10n.n__(
                                '%s restant',
                                '%s restants',
                                credit.remaining,
                                credit.remaining,
                            )
                        }}
                    </span>
                </div>
                <div class="chargebee-quota-dialog__credit-content">
                    <div class="chargebee-quota-dialog__credit-content-input">
                        <div class="number-input-quota-container">
                            <CompassNumberInput
                                :disabled="!credit.enabled"
                                :modelValue="credit.quantity"
                                :step="1"
                                :min="0"
                                @change="(quantity) => onQuantityChanged(credit, quantity)"
                            />
                            <div
                                v-if="!credit.enabled"
                                class="feature-badge-wrapper"
                                @click="handleClickInputContainer(credit)"
                            >
                                <CompassFeatureBadge size="small" variant="upsell" isInteractive />
                            </div>
                        </div>
                        <span class="typographies-paragraph-medium-regular">
                            {{ credit.description }}
                        </span>
                    </div>
                    <span class="typographies-paragraph-medium-regular">
                        {{ credit.price * credit.quantity }} €
                    </span>
                </div>
            </div>
            <div class="chargebee-quota-dialog__total">
                <span class="typographies-paragraph-large-bold">{{ $l10n.__('Total HT') }}</span>
                <span class="typographies-paragraph-large-bold">{{ total }} €</span>
            </div>
            <p
                class="typographies-paragraph-small-regular chargebee-quota-dialog__information-text"
            >
                {{
                    $l10n.__(
                        'En cliquant sur continuer, vous allez être redirigé vers notre partenaire Chargebee pour procéder au paiement. Dès sa validation, vos crédits sont rapidement ajoutés à votre compte.',
                    )
                }}
            </p>
        </template>
    </CompassModal>
</template>

<script>
import _ from 'lodash';
import { createNamespacedHelpers } from 'vuex';

const { mapActions: mapPopupActions } = createNamespacedHelpers('popupPanel');
const { mapState: mapCorpState } = createNamespacedHelpers('corp');
const { mapGetters: mapSubscriptionGetter } = createNamespacedHelpers('subscriptionManager');

export default {
    props: {
        credits: {
            type: Array,
            required: true,
        },
    },
    data() {
        return {
            primaryAction: {
                label: this.$l10n.__('Valider'),
                action: this.onValidate,
                disabled: this.loading || this.total === 0,
            },
            secondariesActions: [
                {
                    label: this.$l10n.__('Annuler'),
                    action: this.onClose,
                },
            ],
            visible: false,
            loading: true,
            dialogCredits: [],
            creditIds: [],
            total: 0,
            shouldDisplayFeatureModal: false,
        };
    },
    computed: {
        ...mapCorpState(['pricingVersion']),
        ...mapSubscriptionGetter(['activePlugins']),
        isStandardMarketing() {
            return this.activePlugins.some((plugin) => {
                return plugin.id === 'marketing' && plugin.plan === 'STARTER';
            });
        },
    },
    created() {
        this.initCreditsData();
    },
    mounted() {
        this.visible = true;
        for (const credit of this.credits) {
            this.creditIds.push(credit.id);
        }
        axios
            .post('/rest/chargebee/charges', this.creditIds)
            .then((response) => {
                this.dialogCredits = this.dialogCredits.map((credit) => {
                    credit.quantity = 0;
                    const creditFromChargebee = response.data.find(
                        (item) => item.item_id === credit.id,
                    );
                    if (creditFromChargebee) {
                        credit.price = creditFromChargebee.price / 100;
                        credit.description = creditFromChargebee.description;
                        //We fetch list of itemsPrice so id of item is itemPriceId and not itemId
                        credit.price_id = creditFromChargebee.id;
                    }
                    return credit;
                });
            })
            .catch(() => {
                this.onClose();
                this.$pushToast({
                    type: 'error',
                    icon: true,
                    text: this.$l10n.__(
                        'Impossible de récupérer les informations des crédits, merci de réessayer ultérieurement',
                    ),
                });
            })
            .finally(() => (this.loading = false));
        for (const item of this.credits) {
            for (const credit of this.dialogCredits) {
                if (item.id === credit.id) {
                    credit.remaining = item.limit - item.used;
                }
            }
        }
    },
    methods: {
        ...mapPopupActions(['open']),
        onQuantityChanged(credit, quantity) {
            credit.quantity = quantity || 0;
            this.recalculateTotal();
        },
        recalculateTotal() {
            this.total = 0;
            for (const credit of this.dialogCredits) {
                this.total += credit.quantity * credit.price;
            }
        },
        initCreditsData() {
            this.dialogCredits = _.cloneDeep(this.credits);
            for (const credit of this.dialogCredits) {
                credit.enabled = true;
                if (this.pricingVersion === 5 && credit.id.includes('marketing')) {
                    credit.enabled = !this.isStandardMarketing;
                }
            }
        },
        onClose() {
            this.$emit('onClose');
        },
        onValidate() {
            const data = this.dialogCredits
                .filter((credit) => credit.quantity > 0)
                .map((credit) => {
                    return {
                        price_id: credit.price_id,
                        quantity: credit.quantity,
                    };
                });
            this.$emit('onValidate', data);
        },
        handleClickInputContainer(credit) {
            if (!credit.enabled) {
                this.open({
                    key: 'featureModal',
                    props: {
                        feature: 'marketing__upsell-contact-sales',
                    },
                });
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.preloader-container__loader:deep() {
    height: 216px;
    width: 100%;

    .compass-loader--medium {
        height: 64px;
        width: 64px;

        .compass-icon--medium {
            height: 64px;
            width: 64px;
        }
    }
}

.chargebee-quota-dialog {
    &__subtitle {
        border-bottom: 1px solid var(--colors-common-border-lighter);
        margin: 0;
        padding-bottom: var(--spacing-3);
    }

    &__credit {
        border-bottom: 1px solid var(--colors-common-border-lighter);
        padding: var(--spacing-3) var(--spacing-1);

        &-title {
            align-items: center;
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            margin-bottom: var(--spacing-1);

            &-name {
                align-items: center;
                display: flex;
                flex-direction: row;
                gap: var(--spacing-0);

                .compass-icon {
                    color: var(--colors-common-icon-neutral);
                }
            }

            &-remaining {
                &--none {
                    color: var(--support-alert-50);
                }
            }
        }

        &-content {
            align-items: center;
            display: flex;
            flex-direction: row;
            justify-content: space-between;

            &-input:deep() {
                align-items: center;
                display: flex;
                flex-direction: row;
                gap: var(--spacing-1);

                span {
                    color: var(--colors-common-text-standard-caption);
                }

                .compass-number-input.compass-number-input--medium {
                    .compass-number-input__input.compass-input__inner {
                        width: 54px;
                        margin: 0 -1px;
                    }
                }
                .number-input-quota-container {
                    position: relative;
                    .feature-badge-wrapper {
                        position: absolute;
                        width: 100%;
                        height: 100%;
                        top: 0;
                        .compass-feature-badge {
                            position: absolute;
                            top: -4px;
                            right: -4px;
                            cursor: pointer;
                        }
                    }
                }
            }
        }
    }

    &__total {
        align-items: center;
        background: var(--colors-common-background-subtle);
        border-radius: 4px;
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        margin: var(--spacing-3) 0;
        padding: var(--spacing-1);
    }

    &__information-text {
        color: var(--colors-common-text-standard-caption);
        margin: 0;
    }
}
</style>
